import {
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, LockKeyhole, UserPlus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  fetchAccountPeople,
  inviteManager,
  removeManager,
  revokeManagerInvitation,
  updateAccountName,
  type AccountPerson,
} from "./api";
import { useSignedInAccount } from "./SignedInShell";

export function Account(): ReactElement {
  const account = useSignedInAccount();
  const queryClient = useQueryClient();
  const [name, setName] = useState(account.name);
  const [saved, setSaved] = useState(false);
  const rename = useMutation({
    mutationFn: updateAccountName,
    onSuccess: (updated) => {
      queryClient.setQueryData(["signed-in-account"], updated);
      setName(updated.name);
      setSaved(true);
    },
  });

  useEffect(() => setName(account.name), [account.name]);
  useEffect(() => {
    const previous = document.title;
    document.title = "Account — Chill";
    return () => {
      document.title = previous;
    };
  }, []);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next = name.trim();
    if (!next || next === account.name || rename.isPending) return;
    setSaved(false);
    rename.mutate(next);
  }

  const canRename = account.role === "owner";

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8 sm:py-12">
      <header className="border-b border-[#cfe4f1] pb-7">
        <h1 className="font-['Avenir_Next',Inter,sans-serif] text-3xl font-semibold tracking-[-0.045em] sm:text-[2.5rem]">
          Account
        </h1>
        <p className="mt-2 max-w-xl text-sm leading-6 text-[#68736e]">
          Your account owns its projects, domains, and billing.
        </p>
      </header>

      <section className="mt-8 overflow-hidden rounded-2xl border border-[#cfe4f1] bg-white/88 shadow-[0_16px_44px_rgba(91,139,174,0.08)] backdrop-blur-sm" aria-labelledby="account-details-title">
        <div className="border-b border-[#dcebf4] px-5 py-4 sm:px-6">
          <h2 id="account-details-title" className="font-semibold">
            Account details
          </h2>
        </div>

        <form onSubmit={submit} className="divide-y divide-[#dcebf4]">
          <div className="grid gap-3 px-5 py-5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:px-6">
            <Label htmlFor="account-name" className="pt-2 text-sm text-[#59635f]">
              Account name
            </Label>
            <div>
              <div className="flex max-w-md flex-col gap-3 sm:flex-row">
                <Input
                  id="account-name"
                  value={name}
                  disabled={!canRename || rename.isPending}
                  maxLength={120}
                  onChange={(event) => {
                    setName(event.currentTarget.value);
                    setSaved(false);
                  }}
                  className="h-10 rounded-xl border-[#c9e0ee] bg-white/85 shadow-none focus-visible:ring-[#b8b9dd]"
                />
                {canRename ? (
                  <Button
                    type="submit"
                    disabled={
                      rename.isPending ||
                      !name.trim() ||
                      name.trim() === account.name
                    }
                    className="shrink-0 rounded-full bg-[#dceffc] text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
                  >
                    {rename.isPending ? "Saving…" : "Save name"}
                  </Button>
                ) : null}
              </div>
              {saved ? (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-[#37685d]" role="status">
                  <Check className="size-3.5" aria-hidden="true" /> Saved
                </p>
              ) : null}
              {rename.isError ? (
                <p className="mt-2 text-xs text-[#a04432]" role="alert">
                  {rename.error.message}
                </p>
              ) : null}
              {!canRename ? (
                <p className="mt-2 text-xs text-[#7a837f]">
                  Only the account owner can change this name.
                </p>
              ) : null}
            </div>
          </div>

          <AccountRow label="Email" value={account.email} />
          <AccountRow
            label="Access"
            value={account.role === "owner" ? "Owner" : "Manager"}
          />
        </form>
      </section>

      <People accountEmail={account.email} canManage={account.role === "owner"} />

      <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#d5e8f4] bg-white/55 px-5 py-4 text-sm text-[#52606e] backdrop-blur-sm">
        <LockKeyhole className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <p className="leading-6">
          Sign-in security is protected by AuthKit.
        </p>
      </div>
    </main>
  );
}

function People({
  accountEmail,
  canManage,
}: {
  accountEmail: string;
  canManage: boolean;
}): ReactElement {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [removeTarget, setRemoveTarget] = useState<AccountPerson | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const people = useQuery({
    queryKey: ["account-people"],
    queryFn: fetchAccountPeople,
  });
  const replacePeople = (next: (current: AccountPerson[]) => AccountPerson[]) => {
    queryClient.setQueryData<AccountPerson[]>(["account-people"], (current = []) =>
      next(current),
    );
  };
  const invite = useMutation({
    mutationFn: inviteManager,
    onSuccess: (person) => {
      replacePeople((current) => [
        ...current.filter((item) => item.id !== person.id),
        person,
      ]);
      setEmail("");
    },
  });
  const revoke = useMutation({
    mutationFn: revokeManagerInvitation,
    onSuccess: (_, invitationId) => {
      replacePeople((current) => current.filter((person) => person.id !== invitationId));
    },
  });
  const remove = useMutation({
    mutationFn: removeManager,
    onSuccess: (_, memberId) => {
      replacePeople((current) => current.filter((person) => person.id !== memberId));
      setRemoveTarget(null);
      setConfirmation("");
    },
  });

  function submitInvite(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next = email.trim();
    if (!next || invite.isPending) return;
    invite.mutate(next);
  }

  const visiblePeople = people.data ?? [];

  return (
    <section className="mt-6 overflow-hidden rounded-2xl border border-[#cfe4f1] bg-white/88 shadow-[0_16px_44px_rgba(91,139,174,0.08)] backdrop-blur-sm" aria-labelledby="people-title">
      <div className="flex flex-col gap-4 border-b border-[#dcebf4] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div>
          <h2 id="people-title" className="font-semibold">People</h2>
          <p className="mt-1 text-xs leading-5 text-[#6d7873]">
            Managers can work across every project in this account.
          </p>
        </div>
        {canManage ? (
          <form className="flex w-full max-w-sm gap-2 sm:w-auto" onSubmit={submitInvite}>
            <Label htmlFor="manager-email" className="sr-only">Manager email</Label>
            <Input
              id="manager-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              placeholder="manager@example.com"
              disabled={invite.isPending}
              className="h-10 min-w-0 rounded-xl border-[#c9e0ee] bg-white/85 shadow-none focus-visible:ring-[#b8b9dd] sm:w-56"
            />
            <Button
              type="submit"
              disabled={!email.trim() || invite.isPending}
              className="h-10 shrink-0 rounded-full bg-[#dceffc] px-4 text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
            >
              <UserPlus aria-hidden="true" />
              {invite.isPending ? "Sending…" : "Invite"}
            </Button>
          </form>
        ) : null}
      </div>

      {people.isPending ? (
        <p className="px-5 py-6 text-sm text-[#6d7873] sm:px-6">Loading people…</p>
      ) : people.isError ? (
        <p className="px-5 py-6 text-sm text-[#a04432] sm:px-6" role="alert">
          {people.error.message}
        </p>
      ) : (
        <div className="divide-y divide-[#dcebf4]">
          {visiblePeople.map((person) => (
            <div key={`${person.status}-${person.id}`} className="flex items-center gap-3 px-5 py-4 sm:px-6">
              <div className="grid size-9 shrink-0 place-items-center rounded-full bg-[#edf6fb] text-sm font-semibold text-[#52606e]">
                {person.email.slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[#20272b]">{person.email}</p>
                <p className="mt-0.5 text-xs capitalize text-[#79837e]">
                  {person.status === "pending" ? "Invitation pending" : person.role}
                  {person.email === accountEmail ? " · You" : ""}
                </p>
              </div>
              {canManage && person.status === "pending" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(person.id)}
                  className="rounded-full text-[#68736e] hover:bg-[#edf6fb]"
                >
                  Revoke
                </Button>
              ) : null}
              {canManage && person.status === "active" && person.role === "manager" ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setRemoveTarget(person);
                    setConfirmation("");
                  }}
                  className="rounded-full text-[#9a4c42] hover:bg-[#fff1ee] hover:text-[#833c33]"
                >
                  Remove
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {removeTarget ? (
        <div className="border-t border-[#efd8d3] bg-[#fff8f6] px-5 py-5 sm:px-6" role="group" aria-label={`Remove ${removeTarget.email}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-[#6f3029]">Remove {removeTarget.email}?</p>
              <p className="mt-1 text-xs leading-5 text-[#80534e]">
                They will immediately lose access to every project in this account.
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Cancel removal"
              onClick={() => setRemoveTarget(null)}
              className="size-8 rounded-full"
            >
              <X aria-hidden="true" />
            </Button>
          </div>
          <Label htmlFor="remove-manager-confirmation" className="mt-4 block text-xs text-[#6f3029]">
            Type their email to confirm
          </Label>
          <div className="mt-2 flex max-w-lg flex-col gap-2 sm:flex-row">
            <Input
              id="remove-manager-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.currentTarget.value)}
              className="h-10 rounded-xl border-[#e7c7c0] bg-white shadow-none"
            />
            <Button
              type="button"
              variant="destructive"
              disabled={confirmation.trim().toLowerCase() !== removeTarget.email.toLowerCase() || remove.isPending}
              onClick={() => remove.mutate(removeTarget.id)}
              className="h-10 rounded-full px-5 shadow-none"
            >
              {remove.isPending ? "Removing…" : "Remove access"}
            </Button>
          </div>
        </div>
      ) : null}

      {invite.isError || revoke.isError || remove.isError ? (
        <p className="border-t border-[#efd8d3] px-5 py-3 text-xs text-[#a04432] sm:px-6" role="alert">
          {(invite.error ?? revoke.error ?? remove.error)?.message}
        </p>
      ) : null}
    </section>
  );
}

function AccountRow({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="grid gap-1 px-5 py-5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:px-6">
      <span className="text-sm text-[#59635f]">{label}</span>
      <span className="text-sm font-medium text-[#20272b]">{value}</span>
    </div>
  );
}
